/**
 * Personal notification channels — Telegram link + notification e-mail
 * (TZ_ASO_Notifications_Delivery §3 / §4).
 *
 * Self-contained block: it owns its polling and its mutations, so it can sit
 * inside the rank-tracker modal once that design lands, or on the Profile
 * tab (its home today). The Save gate of §5 reads `useNotificationChannels`
 * separately — this card only shows and changes the channels.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { describeApiError } from "@/lib/api-error";
import {
  useNotificationEmail,
  useNotificationEmailConfirm,
  useNotificationEmailInit,
  useNotificationEmailTest,
  useTelegramLinkConfirm,
  useTelegramLinkStart,
  useTelegramLinkStatus,
  useTelegramLinkTest,
  useTelegramUnlink,
  type TelegramLinkStart,
} from "@/lib/api-hooks";

export function NotificationChannelsCard({ compact }: { compact?: boolean }) {
  return (
    <div className="stack stack-16">
      <TelegramLinkBlock compact={compact} />
      <NotificationEmailBlock compact={compact} />
    </div>
  );
}

function Note({ tone, children }: { tone: "ok" | "warn" | "err" | "muted"; children: ReactNode }) {
  const color =
    tone === "ok"
      ? "var(--c-pos-600)"
      : tone === "err"
        ? "var(--c-neg-600)"
        : tone === "warn"
          ? "var(--c-warn-500)"
          : "var(--c-text-3)";
  return (
    <div style={{ fontSize: 12.5, color, lineHeight: 1.5 }}>{children}</div>
  );
}

function BlockHead({ title, dot }: { title: string; dot: "ok" | "warn" | "off" }) {
  const bg =
    dot === "ok" ? "var(--c-pos-500)" : dot === "warn" ? "#f59e0b" : "var(--c-text-4)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        aria-hidden
        style={{ width: 8, height: 8, borderRadius: 999, background: bg, display: "inline-block" }}
      />
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{title}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Telegram
// ---------------------------------------------------------------------------

export function TelegramLinkBlock({ compact }: { compact?: boolean }) {
  const status = useTelegramLinkStatus();
  const start = useTelegramLinkStart();
  const test = useTelegramLinkTest();
  const confirm = useTelegramLinkConfirm();
  const unlink = useTelegramUnlink();
  const [issued, setIssued] = useState<TelegramLinkStart | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const st = status.data;
  const state = st?.state ?? "none";

  // The QR is only meaningful while its token is alive; once the link lands
  // (or the token dies) the issued block is stale and goes away.
  useEffect(() => {
    if (state === "linked" || state === "expired" || state === "none") setIssued(null);
  }, [state]);

  const onConnect = async () => {
    setErr(null);
    setTestResult(null);
    try {
      setIssued(await start.mutateAsync());
    } catch (e) {
      setErr(describeApiError(e, "Request failed"));
    }
  };

  const onTest = async () => {
    setErr(null);
    try {
      const r = await test.mutateAsync();
      setTestResult(
        r.delivered
          ? "Test message sent — check Telegram."
          : `Telegram refused the message: ${r.error ?? "unknown error"}`,
      );
    } catch (e) {
      setErr(describeApiError(e, "Request failed"));
    }
  };

  const linked = state === "linked" && st;
  const where =
    linked && st.chat_type === "private"
      ? "your private messages"
      : linked
        ? `the group «${st.chat_title ?? "group"}»`
        : null;
  const dot: "ok" | "warn" | "off" = linked
    ? st.failed_at
      ? "warn"
      : st.verified_at
        ? "ok"
        : "warn"
    : "off";

  return (
    <div className={compact ? "stack stack-8" : "card card-pad stack stack-10"}>
      <BlockHead title="Telegram" dot={dot} />

      {!linked && (
        <>
          <Note tone="muted">
            Get notifications in your private messages, or in your own group:
            add the bot to the group and send it the start command there.
          </Note>
          {issued ? (
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div
                style={{ width: 120, height: 120, flex: "0 0 auto" }}
                dangerouslySetInnerHTML={{ __html: issued.qr_svg }}
              />
              <div className="stack stack-8" style={{ flex: 1, minWidth: 240 }}>
                <a
                  href={issued.deep_link}
                  target="_blank"
                  rel="noreferrer"
                  className="mono"
                  style={{ fontSize: 12.5, wordBreak: "break-all" }}
                >
                  {issued.deep_link}
                </a>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <code className="mono" style={{ fontSize: 12 }}>{issued.start_command}</code>
                  <Btn
                    size="sm"
                    icon={I.copy}
                    onClick={() => void navigator.clipboard?.writeText(issued.start_command)}
                  >
                    Copy
                  </Btn>
                </div>
                <Note tone="muted">
                  For a group: add @{issued.bot_username} to it, then paste the
                  command there. The link works once and expires in 15 minutes.
                  {state === "pending" && " Waiting for Telegram…"}
                </Note>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Btn variant="primary" icon={I.link} onClick={() => void onConnect()} disabled={start.isPending}>
                Connect Telegram
              </Btn>
              {state === "expired" && (
                <Note tone="warn">The previous link expired without a start — issue a new one.</Note>
              )}
            </div>
          )}
        </>
      )}

      {linked && (
        <>
          <Note tone="ok">
            Connected — notifications go to {where}.
          </Note>
          {st.chat_type !== "private" && (
            <Note tone="warn">Everyone in that group will see the positions of your apps.</Note>
          )}
          {st.failed_at && (
            <Note tone="err">
              Delivery stopped after repeated failures
              {st.last_error ? ` (${st.last_error})` : ""}. Send a test and
              confirm it arrived to resume.
            </Note>
          )}
          {!st.verified_at && !st.failed_at && (
            <Note tone="muted">
              Send a test message, then confirm you received it — rules can be
              saved on this channel only after that.
            </Note>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Btn onClick={() => void onTest()} disabled={test.isPending}>
              Send test
            </Btn>
            {(!st.verified_at || st.failed_at) && (
              <Btn
                variant="primary"
                icon={I.check}
                onClick={() => {
                  setErr(null);
                  confirm.mutate(undefined, { onError: (e) => setErr(describeApiError(e, "Request failed")) });
                }}
                disabled={confirm.isPending}
              >
                I received it
              </Btn>
            )}
            <Btn
              variant="danger"
              onClick={() => {
                setErr(null);
                setTestResult(null);
                unlink.mutate(undefined, { onError: (e) => setErr(describeApiError(e, "Request failed")) });
              }}
              disabled={unlink.isPending}
            >
              Disconnect
            </Btn>
          </div>
          {testResult && <Note tone={testResult.startsWith("Test") ? "ok" : "err"}>{testResult}</Note>}
        </>
      )}
      {err && <Note tone="err">{err}</Note>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// E-mail
// ---------------------------------------------------------------------------

export function NotificationEmailBlock({ compact }: { compact?: boolean }) {
  const status = useNotificationEmail();
  const init = useNotificationEmailInit();
  const confirm = useNotificationEmailConfirm();
  const test = useNotificationEmailTest();
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");
  const [note, setNote] = useState<{ tone: "ok" | "warn" | "err"; text: string } | null>(null);

  const st = status.data;
  const smtpOff = st ? !st.smtp_configured : false;
  const dot: "ok" | "warn" | "off" = !st ? "off" : st.failed_at || smtpOff ? "warn" : "ok";

  const onInit = async () => {
    setNote(null);
    try {
      const r = await init.mutateAsync(newEmail.trim());
      setNote(
        r.sent
          ? { tone: "ok", text: `Code sent to ${r.pending_email}. It works for 10 minutes.` }
          : {
              tone: "warn",
              text: "Mail is not configured on this server — the code was not sent. Ask the platform owner to set SMTP.",
            },
      );
    } catch (e) {
      setNote({ tone: "err", text: describeApiError(e, "Request failed") });
    }
  };

  const onConfirm = async () => {
    setNote(null);
    try {
      const r = await confirm.mutateAsync(code.trim());
      setEditing(false);
      setNewEmail("");
      setCode("");
      setNote({ tone: "ok", text: `Notifications will go to ${r.email}.` });
    } catch (e) {
      setNote({ tone: "err", text: describeApiError(e, "Request failed") });
    }
  };

  const onTest = async () => {
    setNote(null);
    try {
      const r = await test.mutateAsync();
      setNote(
        r.sent
          ? { tone: "ok", text: `Test sent to ${r.to}.` }
          : r.smtp_configured
            ? { tone: "err", text: `Could not send to ${r.to} — the mail server refused.` }
            : { tone: "warn", text: "Mail is not configured on this server — nothing was sent." },
      );
    } catch (e) {
      setNote({ tone: "err", text: describeApiError(e, "Request failed") });
    }
  };

  return (
    <div className={compact ? "stack stack-8" : "card card-pad stack stack-10"}>
      <BlockHead title="E-mail" dot={dot} />
      {st && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            className="input"
            readOnly
            value={st.email}
            style={{ flex: 1, minWidth: 220, background: "var(--c-surface-sunk)" }}
          />
          <Btn onClick={() => void onTest()} disabled={test.isPending}>
            Send test
          </Btn>
          <Btn
            icon={I.edit}
            onClick={() => {
              setEditing((v) => !v);
              setNote(null);
              setNewEmail("");
              setCode("");
            }}
          >
            {editing ? "Cancel" : "Change"}
          </Btn>
        </div>
      )}
      {st && (
        <Note tone="muted">
          {st.is_login_email
            ? "Same as your login e-mail. Set a different address for notifications if you like."
            : "Confirmed notification address (login e-mail is separate)."}
          {st.pending_email && !editing && ` A change to ${st.pending_email} is waiting for its code.`}
        </Note>
      )}
      {smtpOff && (
        <Note tone="warn">
          Mail is not configured on this server yet — e-mail notifications will
          be logged, not delivered.
        </Note>
      )}
      {st?.failed_at && (
        <Note tone="err">
          Delivery stopped after repeated failures
          {st.last_error ? ` (${st.last_error})` : ""}. A successful test resumes it.
        </Note>
      )}
      {editing && (
        <div className="stack stack-8">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              className="input"
              type="email"
              placeholder="new-address@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              style={{ flex: 1, minWidth: 220 }}
            />
            <Btn
              variant="primary"
              disabled={!newEmail.includes("@") || init.isPending}
              onClick={() => void onInit()}
            >
              Send code
            </Btn>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              className="input mono"
              placeholder="6-digit code"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ width: 160 }}
            />
            <Btn
              variant="primary"
              icon={I.check}
              disabled={code.replace(/\s/g, "").length < 6 || confirm.isPending}
              onClick={() => void onConfirm()}
            >
              Confirm
            </Btn>
          </div>
          <Note tone="muted">
            Until the code is confirmed, notifications keep going to the current address.
          </Note>
        </div>
      )}
      {note && <Note tone={note.tone}>{note.text}</Note>}
    </div>
  );
}
